import { QueryClient } from '@tanstack/react-query';
import { Procedure, ProcedureResponse } from 'interfaces/procedure';

const EXCLUDING_QUERY_KEYS = [
  'searchTerm',
  'caseIdsToInclude',
  'slideIdsToInclude',
  'features',
  'clinicalData',
  'compositeFilters',
  'datasetJobFilters',
  'uploadFilters',
  'cohortId',
] as const;

const isEmptyParam = (value: string | null) =>
  value == null || value.trim() === '' || value === 'null' || value === '[]' || value === '{}';

const hasValues = (value: unknown): boolean => {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.some(hasValues);
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).some(hasValues);
  return true;
};

const isProcedureResponse = (data: unknown): data is ProcedureResponse => {
  if (!data || typeof data !== 'object') return false;
  const response = data as Partial<ProcedureResponse>;
  return Array.isArray(response.procedures) && typeof response.totalProcedures === 'number';
};

const procedureSignature = (procedures: readonly Procedure[]) =>
  procedures
    .map((procedure) => `${procedure.id}:${(procedure.slides ?? []).map((slide) => slide.id).join(',')}`)
    .sort()
    .join('|');

const readCompletePage = (
  queryKey: readonly unknown[],
  data: unknown,
  studyId: string,
  casesCount: number,
  slidesCount: number
): Procedure[] | null => {
  if (queryKey[0] !== 'procedures' || queryKey.length !== 2 || typeof queryKey[1] !== 'string') return null;
  if (!isProcedureResponse(data)) return null;
  const params = new URLSearchParams(queryKey[1]);
  const hasExcludingParam = Array.from(params.keys()).some((key) =>
    EXCLUDING_QUERY_KEYS.some(
      (excluded) =>
        (key === excluded || key.startsWith(`${excluded}[`) || key.startsWith(`${excluded}.`)) &&
        !isEmptyParam(params.get(key))
    )
  );
  if (hasExcludingParam) return null;

  let filters: unknown;
  try {
    const rawFilters = params.get('filters');
    if (isEmptyParam(rawFilters)) return null;
    filters = JSON.parse(rawFilters as string);
  } catch {
    return null;
  }
  if (!filters || typeof filters !== 'object' || Array.isArray(filters)) return null;
  const filterRecord = filters as Record<string, unknown>;
  if (filterRecord.studyId !== studyId) return null;
  if (Object.keys(filterRecord).some((key) => key !== 'studyId' && hasValues(filterRecord[key]))) return null;
  if (data.proceduresInPreviousPages !== 0 || data.slidesInPreviousPages !== 0) return null;
  if (data.totalProcedures !== casesCount || data.procedures.length !== casesCount) return null;
  if (data.totalSlides !== slidesCount) return null;
  if (new Set(data.procedures.map((procedure) => procedure.id)).size !== data.procedures.length) return null;

  let slideCount = 0;
  for (const procedure of data.procedures) {
    if (procedure.studyId !== studyId || !Array.isArray(procedure.slides)) return null;
    slideCount += procedure.slides.length;
  }
  return slideCount === slidesCount ? data.procedures : null;
};

export const selectCompleteCachedStudyProcedures = (
  queryClient: QueryClient,
  studyId: string,
  casesCount: number | undefined,
  slidesCount: number | undefined
): Procedure[] | null => {
  if (!Number.isFinite(casesCount) || !Number.isFinite(slidesCount)) return null;
  const matches = queryClient
    .getQueriesData<unknown>({ queryKey: ['procedures'] })
    .map(([queryKey, data]) => readCompletePage(queryKey, data, studyId, casesCount as number, slidesCount as number))
    .filter((procedures): procedures is Procedure[] => procedures != null);
  if (!matches.length) return null;
  const signature = procedureSignature(matches[0]);
  return matches.every((procedures) => procedureSignature(procedures) === signature) ? matches[0] : null;
};
